import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import ListenTranslateBar from "@/components/ListenTranslateBar";
import ShareButton from "@/components/ShareButton";
import FarmersNav from "@/components/FarmersNav";
import LivePulseSidebar from "@/components/LivePulseSidebar";
import {
  ArrowLeft, ArrowRight, Plant, MagnifyingGlass, Calendar, Sun,
  Drop, Sparkle, Leaf,
} from "@phosphor-icons/react";
import { fpPath } from "@/lib/site";

/**
 * Canonical grouping so `pulse` and `legume` collapse to one group,
 * `grain` and `cereal` collapse to one group, etc.
 */
const CATEGORY_ALIAS = {
  cereal: "grain",
  legume: "pulse",
  green: "vegetable",
  berry: "fruit",
  nut: "fruit",
  medicinal: "herb",
  spice: "herb",
};

/**
 * Human-friendly labels for crop categories. Falls back to the raw slug.
 */
const CATEGORY_LABELS = {
  grain: "Grains & cereals",
  vegetable: "Vegetables",
  fruit: "Fruits & berries",
  herb: "Herbs & spices",
  pulse: "Pulses & legumes",
  tuber: "Roots & tubers",
  plantation: "Plantation crops",
  oil: "Oil crops",
  fodder: "Fodder & feed crops",
  flower: "Flowers",
  commercial: "Commercial crops",
  other: "Other",
};

const CATEGORY_ORDER = [
  "grain", "vegetable", "fruit", "herb", "pulse", "tuber",
  "oil", "plantation", "commercial", "fodder", "flower", "other",
];

const canonical = (c) => CATEGORY_ALIAS[c] || c || "other";

/**
 * FarmersCrops — /farmers/crops
 * Public crop directory grouped by category with search + climate filter.
 */
export function FarmersCrops() {
  const [crops, setCrops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("");
  const [climate, setClimate] = useState("");
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    http.get("/farmers/crops/categories").then(({ data }) => setCategories(data.categories || []));
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (climate) params.set("climate", climate);
    params.set("limit", "200");
    http.get(`/farmers/crops?${params.toString()}`)
      .then(({ data }) => setCrops(data.crops || []))
      .catch(() => toast.error("Could not load crops"))
      .finally(() => setLoading(false));
  }, [q, climate]);

  // Group crops by canonical category so `pulse`/`legume`, `grain`/`cereal`,
  // etc. collapse to a single heading. Applies the active category filter
  // client-side against the canonical slug.
  const grouped = useMemo(() => {
    const map = {};
    for (const c of crops) {
      const cat = canonical(c.category);
      if (category && cat !== category) continue;
      (map[cat] || (map[cat] = [])).push(c);
    }
    return CATEGORY_ORDER
      .filter((k) => map[k])
      .concat(Object.keys(map).filter((k) => !CATEGORY_ORDER.includes(k)))
      .map((k) => ({ key: k, label: CATEGORY_LABELS[k] || k, crops: map[k] }));
  }, [crops, category]);

  // Category filter chips deduplicated against the same canonical labels.
  const filterChips = useMemo(() => {
    const map = {};
    for (const c of categories) {
      const cat = canonical(c.key);
      if (!map[cat]) map[cat] = { key: cat, count: 0 };
      map[cat].count += c.count || 0;
    }
    return CATEGORY_ORDER
      .filter((k) => map[k])
      .concat(Object.keys(map).filter((k) => !CATEGORY_ORDER.includes(k)))
      .map((k) => map[k]);
  }, [categories]);

  const heroText = "Farmers Pulse crop database — a reusable worldwide catalog of climate, soil and calendar requirements for every crop you might grow.";

  return (
    <main className="bg-[color:var(--np-bg,#F5EBDC)] text-[#151312] min-h-screen pt-32 md:pt-36 pb-32" data-testid="farmers-crops-page">
      <FarmersNav />
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 mt-6">
        <Link to={fpPath("/")} className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#151312]">
          <ArrowLeft size={11} weight="bold" /> Farmers Pulse
        </Link>
        <h1 className="mt-3 font-serif-display text-4xl md:text-5xl leading-[0.98] tracking-tight">Crop database</h1>
        <p className="mt-2 max-w-2xl text-sm text-[#3A3830]">
          A reusable worldwide catalog of climate, soil and calendar requirements. Every entry is a shared entity used across Farmers Pulse and NaturePulse observations.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <ListenTranslateBar text={heroText} testidPrefix="farmers-crops-tools" />
          <ShareButton title="Crop database — Farmers Pulse" text={heroText} variant="chip" testid="farmers-crops-share" />
        </div>

        {/* Filters */}
        <div className="mt-6 flex items-center gap-2 rounded-lg border border-black/15 bg-white px-3 py-2 max-w-lg">
          <MagnifyingGlass size={13} weight="bold" className="text-[#3A3830]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search crops…"
            data-testid="farmers-crops-search"
            className="flex-1 bg-transparent text-sm outline-none"
          />
        </div>

        <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="farmers-crops-filters">
          <button onClick={() => setCategory("")} data-testid="crop-category-all" className={`rounded-full border px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase ${category === "" ? "bg-[#241209] text-[#F5EBDC] border-[#241209]" : "bg-white border-black/15 hover:border-black/40"}`}>All</button>
          {filterChips.map((c) => (
            <button
              key={c.key}
              onClick={() => setCategory(c.key)}
              data-testid={`crop-category-${c.key}`}
              className={`rounded-full border px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase ${category === c.key ? "bg-[#241209] text-[#F5EBDC] border-[#241209]" : "bg-white border-black/15 hover:border-black/40"}`}
            >
              {CATEGORY_LABELS[c.key] || c.key} · {c.count}
            </button>
          ))}
        </div>

        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className="font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[#3A3830]">Climate</span>
          {["tropical", "subtropical", "temperate", "polar"].map((z) => (
            <button
              key={z}
              onClick={() => setClimate(climate === z ? "" : z)}
              data-testid={`crop-climate-${z}`}
              className={`rounded-full border px-3 py-1 font-mono-tech text-[10px] tracking-[0.22em] uppercase ${climate === z ? "bg-[#241209] text-[#F5EBDC] border-[#241209]" : "bg-white border-black/15 hover:border-black/40"}`}
            >{z}</button>
          ))}
        </div>

        {loading ? (
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5 gap-3">
            {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-40 rounded-2xl bg-white/60 animate-pulse" />)}
          </div>
        ) : crops.length === 0 ? (
          <div className="mt-8 rounded-2xl border border-dashed border-black/20 p-10 text-center bg-white/70" data-testid="farmers-crops-empty">
            <Plant size={20} weight="fill" className="mx-auto text-[color:var(--np-brand,#B85F2A)] mb-2" />
            <div className="font-serif-display text-2xl">No crops match your filters.</div>
          </div>
        ) : (
          <div className="mt-8 space-y-10" data-testid="farmers-crops-groups">
            {grouped.map((g) => (
              <section key={g.key} data-testid={`farmers-crops-group-${g.key}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="font-serif-display text-2xl md:text-3xl leading-tight">{g.label}</h2>
                  <span className="font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">{g.crops.length} crops</span>
                </div>
                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {g.crops.map((c) => (
                    <Link
                      key={c.slug}
                      to={fpPath(`/crops/${c.slug}`)}
                      data-testid={`crop-card-${c.slug}`}
                      className="rounded-2xl border border-black/10 bg-white p-4 hover:border-[color:var(--np-brand,#B85F2A)] transition text-left flex gap-3"
                    >
                      <div className="shrink-0 text-4xl leading-none">{c.emoji || "🌱"}</div>
                      <div className="flex-1 min-w-0">
                        <div className="font-serif-display text-lg leading-tight">{c.common_name}</div>
                        {c.scientific_name && <div className="text-[11px] italic text-[#3A3830]">{c.scientific_name}</div>}
                        <div className="mt-1 font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                          {CATEGORY_LABELS[canonical(c.category)] || c.category}
                          {c.has_article && <span className="ml-1 text-emerald-700">· Insights →</span>}
                        </div>
                        {c.article_short_html ? (
                          <div
                            data-testid={`crop-card-teaser-${c.slug}`}
                            className="mt-2 crop-card-teaser text-[13px] leading-snug text-[#241209] line-clamp-4"
                            dangerouslySetInnerHTML={{ __html: c.article_short_html }}
                          />
                        ) : c.notes ? (
                          <p className="mt-2 text-[13px] leading-snug text-[#3A3830] line-clamp-3">{c.notes}</p>
                        ) : null}
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}


/**
 * FarmersCropDetail — /farmers/crops/:slug
 */
export function FarmersCropDetail() {
  const { slug } = useParams();
  const [crop, setCrop] = useState(null);
  const [loading, setLoading] = useState(true);
  const [article, setArticle] = useState(null);

  useEffect(() => {
    setLoading(true);
    http.get(`/farmers/crops/${slug}`)
      .then(({ data }) => setCrop(data.crop || null))
      .catch(() => setCrop(null))
      .finally(() => setLoading(false));
    // Load the human-edited article separately — a 404 just means no
    // article has been pasted yet, so the page still renders the stats.
    http.get(`/farmers/crops/${slug}/article`)
      .then(({ data }) => setArticle(data.article || null))
      .catch(() => setArticle(null));
  }, [slug]);

  if (loading) {
    return <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5"><div className="max-w-[1000px] mx-auto animate-pulse h-64 bg-white/60 rounded-2xl" /></main>;
  }
  if (!crop) {
    return (
      <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10" data-testid="farmers-crop-notfound">
        <div className="max-w-[720px] mx-auto text-center">
          <h1 className="font-serif-display text-3xl">Crop not found</h1>
          <Link to={fpPath("/crops")} className="mt-4 inline-flex text-[color:var(--np-brand,#B85F2A)] underline">Browse the crop database</Link>
        </div>
      </main>
    );
  }

  const readingText = [
    `${crop.common_name}${crop.scientific_name ? " (" + crop.scientific_name + ")" : ""}.`,
    crop.notes || "",
    `Grows well in ${(crop.climate_zones || []).join(", ") || "many climates"}.`,
    crop.temp_c_range?.min !== undefined ? `Temperature range: ${crop.temp_c_range.min}-${crop.temp_c_range.max}°C.` : "",
    crop.rainfall_mm_range?.min !== undefined ? `Rainfall range: ${crop.rainfall_mm_range.min}-${crop.rainfall_mm_range.max} mm.` : "",
    crop.harvest_days ? `Typical harvest cycle: ${crop.harvest_days} days.` : "",
  ].filter(Boolean).join(" ");

  const MONTH = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

  return (
    <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10" data-testid="farmers-crop-detail">
      <div className="max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-8">
        <div className="min-w-0">
        <Link to={fpPath("/crops")} className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#151312]">
          <ArrowLeft size={11} weight="bold" /> Crop database
        </Link>
        <header className="mt-4 rounded-2xl border border-black/10 bg-white p-6 md:p-8">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="text-6xl">{crop.emoji || "🌱"}</div>
            <div className="flex-1 min-w-[220px]">
              <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                {crop.category} crop
              </div>
              <h1 className="mt-1 font-serif-display text-4xl md:text-5xl leading-[0.98] tracking-tight" data-testid="crop-name">{crop.common_name}</h1>
              {crop.scientific_name && <div className="mt-1 text-sm italic text-[#3A3830]">{crop.scientific_name}</div>}
              {(crop.climate_zones || []).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {crop.climate_zones.map((z) => (
                    <span key={z} className="rounded-full bg-[#FBF2E1] border border-black/10 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase">{z}</span>
                  ))}
                </div>
              )}
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <ListenTranslateBar text={readingText} testidPrefix="crop-tools" />
                <ShareButton title={crop.common_name} text={crop.notes || crop.common_name} variant="chip" testid="crop-share" />
                <Link to={fpPath("/calendar")} data-testid="crop-calendar-link" className="inline-flex items-center gap-1 rounded-full border border-black/15 bg-white px-3 py-1.5 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5">
                  <Calendar size={11} weight="fill" /> Add to calendar
                </Link>
              </div>
            </div>
          </div>
          {crop.notes && <p className="mt-5 text-sm text-[#3A3830] whitespace-pre-wrap leading-relaxed">{crop.notes}</p>}
        </header>

        {/* Hero image — from Unsplash / Wikimedia Commons — respects licence attribution */}
        {article?.hero_image_url && (
          <figure className="mt-6 rounded-2xl overflow-hidden border border-black/10 bg-white" data-testid="crop-hero-image">
            <img
              src={article.hero_image_url}
              alt={article.hero_image_alt || crop.common_name}
              className="block w-full h-[280px] md:h-[420px] object-cover"
              loading="lazy"
            />
            {(article.hero_image_credit || article.hero_image_source) && (
              <figcaption className="px-4 py-2 flex items-center justify-between gap-3 flex-wrap border-t border-black/10 font-mono-tech text-[10px] tracking-[0.24em] uppercase text-[#3A3830]">
                <span data-testid="crop-hero-credit">
                  {article.hero_image_credit || "Photo credit"}
                </span>
                {article.hero_image_source && (
                  <a
                    href={article.hero_image_source}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[color:var(--np-brand,#B85F2A)] hover:underline"
                    data-testid="crop-hero-source"
                  >
                    View source ↗
                  </a>
                )}
              </figcaption>
            )}
          </figure>
        )}

        {/* Stats */}
        <section className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          {crop.temp_c_range?.min !== undefined && (
            <Stat icon={Sun} label="Temperature" value={`${crop.temp_c_range.min}–${crop.temp_c_range.max}°C`} />
          )}
          {crop.rainfall_mm_range?.min !== undefined && (
            <Stat icon={Drop} label="Rainfall" value={`${crop.rainfall_mm_range.min}–${crop.rainfall_mm_range.max} mm`} />
          )}
          {crop.harvest_days && <Stat icon={Calendar} label="Cycle" value={`${crop.harvest_days} days`} />}
          {crop.germination_days && <Stat icon={Sparkle} label="Germinates" value={`${crop.germination_days} days`} />}
        </section>

        {/* Planting windows */}
        {((crop.planting_north || []).length > 0 || (crop.planting_south || []).length > 0) && (
          <section className="mt-6 rounded-2xl border border-black/10 bg-white p-5">
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">Planting windows</div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
              <MonthStrip label="Northern hemisphere" months={crop.planting_north || []} />
              <MonthStrip label="Southern hemisphere" months={crop.planting_south || []} />
            </div>
            <div className="mt-3 text-[10px] font-mono-tech tracking-[0.22em] uppercase text-[#3A3830]">
              These windows are guides only. Local frost dates, monsoon timing and variety-specific advice always take precedence.
            </div>
          </section>
        )}

        {/* Pests / Diseases */}
        {((crop.pests || []).length > 0 || (crop.diseases || []).length > 0) && (
          <section className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {(crop.pests || []).length > 0 && (
              <div className="rounded-2xl border border-black/10 bg-white p-5">
                <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">Common pests</div>
                <ul className="mt-2 flex flex-wrap gap-1.5">
                  {(crop.pests || []).map((p) => <li key={p} className="rounded-full bg-[#FBF2E1] border border-black/10 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase">{p}</li>)}
                </ul>
              </div>
            )}
            {(crop.diseases || []).length > 0 && (
              <div className="rounded-2xl border border-black/10 bg-white p-5">
                <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">Common diseases</div>
                <ul className="mt-2 flex flex-wrap gap-1.5">
                  {(crop.diseases || []).map((p) => <li key={p} className="rounded-full bg-[#FBF2E1] border border-black/10 px-2 py-0.5 font-mono-tech text-[9px] tracking-[0.22em] uppercase">{p}</li>)}
                </ul>
              </div>
            )}
          </section>
        )}

        {/* Human-edited article (pasted by editors in the admin console). */}
        {article && (
          <article
            className="mt-10 rounded-2xl border border-black/10 bg-white p-6 md:p-10"
            data-testid="crop-article"
          >
            <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
              Crop Insights
            </div>
            <h2
              className="mt-1 font-serif-display text-3xl md:text-4xl leading-[1.05] tracking-tight"
              data-testid="crop-article-title"
            >
              {article.title}
            </h2>
            {article.subtitle && (
              <p className="mt-2 font-serif-display italic text-lg text-[#3A3830] max-w-2xl">
                {article.subtitle}
              </p>
            )}

            {/* Short teaser — always at the top when present */}
            {article.short_html && (
              <div
                className="mt-5 rounded-2xl bg-[#FBF2E1] p-5 crop-article-body max-w-3xl"
                data-testid="crop-article-short"
                dangerouslySetInnerHTML={{ __html: article.short_html }}
              />
            )}

            {(article.hero_facts || []).length > 0 && (
              <ul
                className="mt-5 grid sm:grid-cols-3 gap-3"
                data-testid="crop-article-hero"
              >
                {article.hero_facts.map((f, i) => (
                  <li
                    key={i}
                    className="rounded-2xl border border-black/10 bg-[#FBF2E1] p-3 text-sm"
                  >
                    <Sparkle
                      size={11}
                      weight="fill"
                      className="inline mr-1 text-[color:var(--np-brand,#B85F2A)]"
                    />
                    {f}
                  </li>
                ))}
              </ul>
            )}

            {/* Long article — prefer HTML (rich editor), fall back to markdown */}
            {article.body_html ? (
              <div
                className="mt-6 max-w-3xl font-serif-display text-[17px] leading-[1.75] text-[#241209] crop-article-body"
                data-testid="crop-article-body"
                dangerouslySetInnerHTML={{ __html: article.body_html }}
              />
            ) : article.body_md ? (
              <div
                className="mt-6 max-w-3xl font-serif-display text-[17px] leading-[1.75] text-[#241209] crop-article-body"
                data-testid="crop-article-body"
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {article.body_md}
                </ReactMarkdown>
              </div>
            ) : null}

            {(article.further_reading || []).length > 0 && (
              <section className="mt-8 border-t border-black/10 pt-4">
                <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[color:var(--np-brand,#B85F2A)]">
                  Further reading
                </div>
                <ul className="mt-2 space-y-1 text-sm text-[#3A3830]">
                  {article.further_reading.map((r, i) => (
                    <li key={i} className="pl-3 border-l-2 border-black/10">
                      {/^https?:\/\//i.test(r) ? (
                        <a
                          href={r}
                          target="_blank"
                          rel="noreferrer"
                          className="underline text-[color:var(--np-brand,#B85F2A)]"
                        >
                          {r}
                        </a>
                      ) : r}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <div className="mt-6 flex items-center gap-3 flex-wrap font-mono-tech text-[9px] tracking-[0.24em] uppercase text-[#3A3830]">
              <span>Updated {article.updated_at?.slice(0, 10)}</span>
              {article.word_count > 0 && <span>· {article.word_count} words</span>}
              <ShareButton
                title={article.title}
                text={article.subtitle || article.title}
                variant="chip"
                testid="crop-article-share"
              />
            </div>
          </article>
        )}
        </div>
        <div className="lg:sticky lg:top-6 self-start">
          <LivePulseSidebar slug={slug} />
        </div>
      </div>
    </main>
  );

  function MonthStrip({ label, months }) {
    return (
      <div>
        <div className="font-mono-tech text-[10px] tracking-[0.22em] uppercase text-[#3A3830]">{label}</div>
        <div className="mt-2 grid grid-cols-12 gap-1" data-testid={`crop-month-strip-${label.toLowerCase().startsWith("northern") ? "north" : "south"}`}>
          {MONTH.map((m, i) => {
            const active = months.includes(i + 1);
            return (
              <div key={m} data-testid={`crop-month-${label.toLowerCase().startsWith("northern") ? "north" : "south"}-${i + 1}`} data-active={active ? "true" : "false"} className={`text-center rounded py-1 font-mono-tech text-[9px] tracking-[0.16em] uppercase ${active ? "bg-[color:var(--np-brand,#B85F2A)] text-white" : "bg-[#FBF2E1]/50 text-[#3A3830] border border-black/5"}`}>
                {m[0]}
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg border border-black/10 bg-white p-3">
      <div className="font-mono-tech text-[9px] tracking-[0.28em] uppercase text-[#3A3830] flex items-center gap-1">
        <Icon size={11} weight="fill" className="text-[color:var(--np-brand,#B85F2A)]" /> {label}
      </div>
      <div className="mt-1 font-serif-display text-base">{value || "—"}</div>
    </div>
  );
}


/**
 * FarmersCalendar — /farmers/calendar
 * Location-aware planting calendar with hemisphere + climate zone hints.
 */
export function FarmersCalendar() {
  const [loc, setLoc] = useState(() => {
    try { const raw = localStorage.getItem("np:pulse-location"); return raw ? JSON.parse(raw) : null; }
    catch { return null; }
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = new URLSearchParams();
    if (loc?.lat) { params.set("latitude", loc.lat); params.set("longitude", loc.lon); }
    http.get(`/farmers/calendar?${params.toString()}`)
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [loc?.lat, loc?.lon]);

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Geolocation is not supported here.");
    navigator.geolocation.getCurrentPosition((p) => {
      const l = { lat: p.coords.latitude, lon: p.coords.longitude, label: "My location" };
      setLoc(l);
      try { localStorage.setItem("np:pulse-location", JSON.stringify(l)); } catch { /* noop */ }
    }, () => toast.error("Could not read your location."));
  };

  const rows = data?.crops || [];
  const groups = useMemo(() => ({
    sow: rows.filter((r) => r.what_to_do_now === "sow"),
    harvest: rows.filter((r) => r.what_to_do_now === "harvest"),
    grow: rows.filter((r) => r.what_to_do_now === "grow"),
    rest: rows.filter((r) => r.what_to_do_now === "rest"),
  }), [rows]);

  const heroText = `Farmers Pulse calendar. Locate yourself and the calendar reshuffles by hemisphere and climate zone. ${data ? `You are in a ${data.climate_zone} climate, ${data.hemisphere}ern hemisphere, month ${data.current_month}.` : ""}`;

  return (
    <main className="bg-[#F5EBDC] min-h-screen pt-32 md:pt-36 pb-32 px-5 md:px-10" data-testid="farmers-calendar-page">
      <div className="max-w-[1200px] mx-auto">
        <Link to={fpPath("/")} className="inline-flex items-center gap-1.5 font-mono-tech text-[10px] tracking-[0.32em] uppercase text-[#3A3830] hover:text-[#151312]">
          <ArrowLeft size={11} weight="bold" /> Farmers Pulse
        </Link>
        <h1 className="mt-3 font-serif-display text-4xl md:text-5xl leading-[0.98] tracking-tight">Planting calendar</h1>
        <p className="mt-2 max-w-2xl text-sm text-[#3A3830]">
          Locate yourself and the calendar reshuffles by hemisphere and climate zone. A guide, not a rule — traditional knowledge and local extension advice always take precedence.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <ListenTranslateBar text={heroText} testidPrefix="farmers-calendar-tools" />
          <ShareButton title="Farmers Pulse calendar" text={heroText} variant="chip" testid="farmers-calendar-share" />
          <button
            onClick={useMyLocation}
            data-testid="farmers-calendar-use-location"
            className="inline-flex items-center gap-1.5 rounded-full border border-black/20 bg-white px-4 py-2 font-mono-tech text-[10px] tracking-[0.28em] uppercase hover:bg-black/5"
          >
            {loc?.lat ? "Update location" : "Use my location"}
          </button>
        </div>
        {data && (
          <div className="mt-3 font-mono-tech text-[10px] tracking-[0.28em] uppercase text-[#3A3830]">
            {data.climate_zone} climate · {data.hemisphere} hemisphere · month {data.current_month}
          </div>
        )}

        {loading ? (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-40 rounded-2xl bg-white/60 animate-pulse" />)}
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="farmers-calendar-groups">
            <Group testid="calendar-group-sow" title="Sow now" items={groups.sow} tone="bg-emerald-50 border-emerald-200 text-emerald-800" />
            <Group testid="calendar-group-harvest" title="Harvest now" items={groups.harvest} tone="bg-amber-50 border-amber-200 text-amber-800" />
            <Group testid="calendar-group-grow" title="Growing" items={groups.grow} tone="bg-sky-50 border-sky-200 text-sky-800" />
            <Group testid="calendar-group-rest" title="Rest / off-season" items={groups.rest} tone="bg-stone-100 border-stone-200 text-stone-800" />
          </div>
        )}
      </div>
    </main>
  );
}

function Group({ title, items, tone, testid }) {
  return (
    <section className={`rounded-2xl border p-5 ${tone}`} data-testid={testid}>
      <div className="font-mono-tech text-[10px] tracking-[0.32em] uppercase">{title}</div>
      <div className="mt-1 font-serif-display text-2xl">{items.length} crops</div>
      {items.length === 0 ? (
        <div className="mt-2 text-sm">None for your climate this month.</div>
      ) : (
        <ul className="mt-3 space-y-1.5">
          {items.slice(0, 12).map((c) => (
            <li key={c.slug} className="flex items-center gap-2">
              <span className="text-lg">{c.emoji || "🌱"}</span>
              <Link to={fpPath(`/crops/${c.slug}`)} className="font-serif-display text-base hover:underline">
                {c.common_name}
              </Link>
              <span className="ml-auto text-[10px] font-mono-tech tracking-[0.24em] uppercase opacity-70">
                {c.category}
              </span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
